/**
 * @file: useInfiniteList.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { uniq } from '@euler/utils/array';
import { usePersistFn } from '@euler/utils/hooks';
import produce, { Draft } from 'immer';
import { useRef, useState } from 'react';

export type InfiniteListStatus = {
  isRefreshing: boolean;
  isLoading: boolean;
  error: Error | undefined;
  hasMore: boolean;
  loadedDataLength: number;
  offset: number;
  limit: number;
};

export type InfiniteListState<T, U = any> = {
  fetch: (...params: U[]) => Promise<void>;
  refresh: (...params: U[]) => Promise<void>;
  fetchWithoutRefresh: (...params: U[]) => Promise<void>;
  invalidate: (index: number, ...params: U[]) => Promise<void>;
  replace: (...params: U[]) => Promise<void>;
  fetchMore: (...params: U[]) => Promise<void>;
  fetchSinceLastError: (...params: U[]) => Promise<void>;
  data: T[];
  status: InfiniteListStatus;
  update: (handler: (item: Draft<T>) => undefined | 'remove') => void;
};

const combineData = <T>(
  current: T[],
  next: T[],
  keyExtractor?: (item: T) => string,
): T[] => {
  if (keyExtractor == null) {
    return current.concat(next);
  }
  return uniq(current.concat(next), keyExtractor);
};

export const useInfiniteList = <T, U = any>({
  limit,
  query,
  initialLoading,
  keyExtractor,
}: {
  query: (offset: number, ...params: U[]) => Promise<T[]>;
  limit: number;
  initialLoading?: boolean;
  keyExtractor?: (item: T) => string;
}): InfiniteListState<T> => {
  const [state, setState] = useState<{
    loading: boolean;
    error?: Error;
    data: T[];
    lastOffset?: number;
  }>({
    loading: initialLoading ?? false,
    error: undefined,
    data: [],
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const requestId = useRef<number>();
  const queryData = usePersistFn(
    async (
      params: U[],
      currentData: T[],
      nextOffset: number,
      mergeData: (current: T[], next: T[]) => T[],
      refresh = false,
      nextRequestId: number | undefined = undefined,
    ) => {
      if (refresh) {
        setIsRefreshing(true);
      }
      setState({ loading: true, error: undefined, data: currentData });
      try {
        const next = await query(nextOffset, ...params);

        // discard previous request results.
        if (nextRequestId != null && nextRequestId !== requestId.current) {
          return;
        }

        const data = mergeData(currentData, next);
        setState({ loading: false, error: undefined, data });
        setOffset(nextOffset);
        setHasMore(next.length === limit);
      } catch (e) {
        console.error(e);
        setState({
          loading: false,
          error: e as Error,
          data: currentData,
          lastOffset: nextOffset,
        });
      } finally {
        if (refresh) {
          setIsRefreshing(false);
        }
      }
    },
  );

  const fetch = usePersistFn(async (...params: U[]) => {
    await queryData(params, state.data, 0, (_, results) => results, false);
  });

  const fetchWithoutRefresh = usePersistFn(async (...params: U[]) => {
    await queryData(params, state.data, 0, (_, results) => results, false);
  });

  const invalidate = usePersistFn(async (index: number, ...params: U[]) => {
    const startOffset = Math.floor(index / limit);
    await queryData(
      params,
      state.data,
      startOffset,
      (current, results) =>
        combineData(current.slice(0, startOffset), results, keyExtractor),
      true,
    );
  });

  const refresh = usePersistFn(async (...params: U[]) => {
    await queryData(params, state.data, 0, (_, results) => results, true);
  });

  const replace = usePersistFn(async (...params: U[]) => {
    requestId.current = Math.random();
    await queryData(
      params,
      [],
      0,
      (_, results) => results,
      false,
      requestId.current,
    );
  });

  const fetchMore = usePersistFn(async (...params: U[]) => {
    if (!hasMore || state.loading) return;
    await queryData(params, state.data, offset + limit, (x, y) =>
      combineData(x, y, keyExtractor),
    );
  });

  const fetchSinceLastError = usePersistFn(async (...params: U[]) => {
    if (state.lastOffset == null) return;
    await queryData(params, state.data, state.lastOffset, (x, y) =>
      combineData(x, y, keyExtractor),
    );
  });

  const update = usePersistFn(
    (handler: (item: Draft<T>) => undefined | 'remove') => {
      const updatedState = produce(state, draft => {
        for (let i = draft.data.length - 1; i >= 0; i--) {
          const item = draft.data[i];
          const res = handler(item);
          if (res === 'remove') {
            draft.data.splice(i, 1);
          }
        }
      });
      if (updatedState !== state) {
        setState(updatedState);
      }
    },
  );

  return {
    fetch,
    refresh,
    fetchWithoutRefresh,
    invalidate,
    replace,
    fetchMore,
    fetchSinceLastError,
    data: state.data,
    update,
    status: {
      offset,
      limit,
      isRefreshing,
      isLoading: state.loading,
      error: state.error,
      hasMore,
      loadedDataLength: state.data.length,
    },
  };
};
